import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { searchPatients, searchProtocols, searchOrders } from '../api/searchApi';
import { debounce } from '../utils/debounce';

const SearchContext = createContext();

export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
};

export function SearchProvider({ children }) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState({
    patients: [],
    protocols: [],
    orders: [],
    loading: false,
    error: null,
  });
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const searchRef = useRef(null);

  // Debounced search function
  const performSearch = useCallback(
    debounce(async query => {
      if (!query || query.length < 2) {
        setSearchResults({
          patients: [],
          protocols: [],
          orders: [],
          loading: false,
          error: null,
        });
        return;
      }

      setSearchResults(prev => ({ ...prev, loading: true, error: null }));

      try {
        // Perform searches in parallel
        const [patientsRes, protocolsRes, ordersRes] = await Promise.allSettled([
          searchPatients(query),
          searchProtocols(query),
          searchOrders(query),
        ]);

        setSearchResults({
          patients: patientsRes.status === 'fulfilled' ? patientsRes.value.slice(0, 5) : [],
          protocols: protocolsRes.status === 'fulfilled' ? protocolsRes.value.slice(0, 5) : [],
          orders: ordersRes.status === 'fulfilled' ? ordersRes.value.slice(0, 5) : [],
          loading: false,
          error: null,
        });
      } catch (error) {
        console.error('Search error:', error);
        setSearchResults(prev => ({
          ...prev,
          loading: false,
          error: 'Failed to perform search',
        }));
      }
    }, 300),
    []
  );

  const handleSearch = useCallback(
    query => {
      setSearchQuery(query);
      performSearch(query);
    },
    [performSearch]
  );

  const handleResultClick = useCallback(
    (type, item) => {
      setIsSearchOpen(false);
      setSearchQuery('');
      setSearchResults({
        patients: [],
        protocols: [],
        orders: [],
        loading: false,
        error: null,
      });

      // Navigate based on result type
      switch (type) {
        case 'patient':
          navigate(`/patients/${item.id}`);
          break;
        case 'protocol':
          navigate(`/protocols/${item.id}`);
          break;
        case 'order':
          navigate(`/orders/${item.id}`);
          break;
        default:
          break;
      }
    },
    [navigate]
  );

  const value = {
    searchQuery,
    searchResults,
    isSearchOpen,
    searchRef,
    handleSearch,
    handleResultClick,
    setIsSearchOpen,
  };

  return <SearchContext.Provider value={value}>{children}</SearchContext.Provider>;
}
